import { Flex, FormGroup, FormLabel, Grid } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import { languagesList } from "i18n";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { FormikSelect } from "@/components/Select";
import { FormikForm } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";

import { IUserFormikProps } from "../types";

export const validationLanguageSchema = Yup.object().shape({
  language: Yup.string().nullable().required(VALIDATION_TYPES.REQUIRED),
});

export interface ILanguageFormValues {
  language: string;
}

export const MESSENGER_CONTEXT = "app-language-form";

interface ILanguageFormComponentProps extends IEditableSectionRenderProps {
  form: FormikProps<ILanguageFormValues>;
  onCancel: () => void;
}

export const LanguageForm = memo((props: ILanguageFormComponentProps) => {
  const { form } = props;

  const { t } = useTranslation("session");

  useEditableSubmit(props.form, props.editableProps);

  const languages = Object.values(languagesList);

  return (
    <FormikForm>
      <Flex flexDirection="column" gap="md" flexGrow={1} mt={["xl", null, null, "0"]}>
        <Messenger context={MESSENGER_CONTEXT} />

        <Grid columns={["1fr", null, "1fr 1fr", "1fr 1fr 1fr"]} gap="md">
          <Flex flexDirection="column" flexGrow={1}>
            <FormGroup
              label={<FormLabel required label={t("USERS.SETTINGS.USER_ACCOUNT.LANGUAGE")} />}
              error={<FormikError name="language" data-test="error-language" />}>
              <FormikSelect
                name="language"
                testName="language"
                value={form.values.language}
                required
                options={languages}
                size="md"
                simpleValue
                isClearable={false}
              />
            </FormGroup>
          </Flex>
        </Grid>
      </Flex>
    </FormikForm>
  );
});

export interface ILanguageFormikProps extends IUserFormikProps<ILanguageFormValues>, ILanguageFormComponentProps {}

export const LanguageFormik: React.FunctionComponent<ILanguageFormikProps> = React.memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(form: FormikProps<ILanguageFormValues>) => <LanguageForm {...rest} form={form} />}
    </Formik>
  ),
);
